/**
 * API Client for Concierge API (adapted from reference frontend)
 *
 * Connects to concierge-api backend (Flask server at localhost:5001)
 * CRITICAL: Session ID handling - read from first response, pass to all subsequent requests
 */

// Empty default → relative URLs (e.g. `/api/health`) leverage Vite's dev proxy.
// Set VITE_API_BASE_URL to an absolute host (no `/api` suffix) for production builds.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Health check endpoint
 */
export async function checkHealth() {
  try {
    const response = await fetch(`${API_BASE_URL}/api/health`);
    if (!response.ok) {
      throw new Error(`Health check failed: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Health check error:', error);
    throw error;
  }
}

/**
 * Ask a question to the RAG agent
 *
 * CRITICAL: Session ID handling
 * - First request: sessionId is null, API generates and returns session_id
 * - Subsequent requests: pass the session_id from previous response
 * - API always echoes back session_id (either generated or provided)
 *
 * @param {string} question - The question to ask
 * @param {string} clientId - The client organization ID
 * @param {string|null} sessionId - Session ID from previous response (null for first request)
 * @returns {Promise<{answer: string, citations: Array, confidence: string, reasoning: string, session_id: string}>}
 */
export async function askQuestion(question, clientId, sessionId = null) {
  try {
    const requestBody = {
      question,
      client_id: clientId,
    };

    // IMPORTANT: Only include session_id if it exists
    // This allows the API to create a new session on first request
    if (sessionId) {
      requestBody.session_id = sessionId;
    }

    const response = await fetch(`${API_BASE_URL}/api/ask`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `API error: ${response.status}`);
    }

    const data = await response.json();

    // API should return session_id in response
    // Components should save this for subsequent requests
    return data;
  } catch (error) {
    console.error('API error:', error);
    throw error;
  }
}

/**
 * Send a chat message to the assistant (alias for askQuestion)
 * This matches the reference frontend API signature
 *
 * @param {string} clientId - Client ID (org_slug in reference)
 * @param {string} message - User message
 * @param {string|null} personaId - Demo persona ID (unused in our API for now)
 * @param {string|null} sessionId - Session ID from previous response
 * @returns {Promise<{response: string, session_id: string, citations: Array, confidence: string, reasoning: string}>}
 */
export async function sendChatMessage(clientId, message, personaId = null, sessionId = null) {
  try {
    const apiResponse = await askQuestion(message, clientId, sessionId);

    // Transform response to match reference frontend expectations
    // Reference expects: { response: string, session_id: string }
    // Our API returns: { answer: string, citations: Array, confidence: string, reasoning: string, session_id: string }

    // Enforce API contract: session_id must be present
    if (!apiResponse.session_id) {
      console.error('API contract violation: No session_id in response', apiResponse);
      throw new Error('API did not return required session_id');
    }

    return {
      response: apiResponse.answer,
      session_id: apiResponse.session_id,
      citations: apiResponse.citations,
      confidence: apiResponse.confidence,
      reasoning: apiResponse.reasoning,
    };
  } catch (error) {
    console.error('Chat error:', error);
    throw error;
  }
}

/**
 * Generate a session ID for conversation tracking
 */
export function generateSessionId() {
  return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Get current ISO week number
 * Returns: 1-52 based on ISO 8601 week date
 *
 * ISO weeks start on Monday. Week 1 is the week containing Jan 4
 * (or equivalently, the week containing the first Thursday of the year).
 */
export function getCurrentWeekNumber() {
  const now = new Date();

  // Set to nearest Thursday (ISO week definition)
  const target = new Date(now.valueOf());
  const dayNum = (now.getDay() + 6) % 7; // Make Monday = 0, Sunday = 6
  target.setDate(target.getDate() - dayNum + 3); // Set to Thursday of current week

  // Get first Thursday of the year
  const firstThursday = new Date(target.getFullYear(), 0, 1);
  if (firstThursday.getDay() !== 4) {
    // Move to first Thursday
    firstThursday.setMonth(0, 1 + ((4 - firstThursday.getDay() + 7) % 7));
  }

  // Calculate week number
  const weekNum = 1 + Math.round((target - firstThursday) / (7 * 24 * 60 * 60 * 1000));
  return Math.min(weekNum, 52); // Cap at 52
}

/**
 * Get spotlight data for a client
 *
 * Reads from database via API endpoint with JSON fallback.
 *
 * @param {string} clientId - Client ID (e.g., 'aboncle')
 * @returns {Promise<Array>} - Array of 52 week objects with spots
 */
export async function getSpotlight(clientId) {
  try {
    // Fetch from API endpoint (uses admin key internally)
    const dbResponse = await fetch(`${API_BASE_URL}/api/spotlights/${clientId}`);

    if (dbResponse.ok) {
      const dbData = await dbResponse.json();

      if (dbData && dbData.length > 0) {
        console.log(`✨ Loaded ${dbData.length} spotlight weeks from database for ${clientId}`);

        // Transform database format to match expected format
        return dbData.map((week) => ({
          week: week.week,
          name: week.name,
          period: week.period,
          emoji: week.emoji,
          header: week.header,
          spots: week.spots || [],
          hot_topics: week.hot_topics || [],
        }));
      }
    }

    // Fallback to JSON if database is empty or fails
    console.log(`✨ Database empty or unavailable, falling back to JSON for ${clientId}`);
    const jsonResponse = await fetch(`/data/clients/${clientId}/spotlight.json`);
    if (!jsonResponse.ok) {
      throw new Error(`Failed to load spotlight: ${jsonResponse.statusText}`);
    }
    return await jsonResponse.json();
  } catch (error) {
    console.error('Spotlight error:', error);
    // Final fallback: try JSON
    try {
      const jsonResponse = await fetch(`/data/clients/${clientId}/spotlight.json`);
      if (jsonResponse.ok) {
        console.log(`✨ Error fallback: loaded from JSON for ${clientId}`);
        return await jsonResponse.json();
      }
    } catch (fallbackError) {
      console.error('JSON fallback also failed:', fallbackError);
    }
    throw error;
  }
}

/**
 * Get current week spotlight data
 * Supports URL override: ?wk=4 to test specific weeks
 *
 * @param {string} clientId - Client ID
 * @returns {Promise<Object|null>} - Current week object with spots or null
 */
export async function getCurrentWeek(clientId) {
  try {
    const spotlight = await getSpotlight(clientId);

    // Check for URL parameter override (for testing)
    const urlParams = new URLSearchParams(window.location.search);
    const weekOverride = urlParams.get('wk');

    let currentWeekNum;
    if (weekOverride) {
      // Use override week number from URL
      currentWeekNum = parseInt(weekOverride, 10);
      console.log(`✨ Week override from URL: ${currentWeekNum}`);
    } else {
      // Use actual current week based on date
      currentWeekNum = getCurrentWeekNumber();
    }

    return spotlight.find((week) => week.week === currentWeekNum) || null;
  } catch (error) {
    console.error('Current week error:', error);
    return null;
  }
}

/**
 * Submit member feedback for an AI response
 *
 * @param {string} requestId - The trace request_id
 * @param {string} clientId - Client ID (e.g., 'aboncle')
 * @param {string} feedback - 'thumbs_up' or 'thumbs_down'
 * @param {string} reason - Optional reason text
 * @returns {Promise<Object>} - { success: true, message: '...' }
 */
export async function submitFeedback(requestId, clientId, feedback, reason = '', extras = {}) {
  try {
    const body = {
      request_id: requestId,
      client_id: clientId,
      feedback: feedback,
      reason: reason,
    };
    if (extras.contact_requested) {
      body.contact_requested = true;
    }
    if (extras.contact_email) {
      body.contact_email = extras.contact_email;
    }

    const response = await fetch(`${API_BASE_URL}/api/feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `API error: ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ Feedback submitted: ${feedback} for request ${requestId.substring(0, 20)}...`);
    return data;
  } catch (error) {
    console.error('Feedback submission error:', error);
    throw error;
  }
}

// ============================================================================
// UI Blocks API
// ============================================================================

/**
 * Fetch list of enabled blocks for a client (lightweight)
 *
 * This returns just the block IDs and order - each block then fetches
 * its own data independently for async loading.
 *
 * @param {string} clientId - Client identifier
 * @param {object} options - Optional settings
 * @param {string} options.authSessionId - Auth session ID for authenticated requests
 * @param {string} options.location - Display location filter ("landing" or "conversation")
 * @returns {Promise<{blocks: Array<{id: string, order: number}>}>}
 */
export async function fetchEnabledBlocks(clientId, options = {}) {
  try {
    const params = new URLSearchParams({ client_id: clientId });
    const headers = {};

    // Add location filter if provided (server defaults to "landing")
    if (options.location) {
      params.append('location', options.location);
    }

    // Add auth session header if provided
    if (options.authSessionId) {
      headers['X-Auth-Session-Id'] = options.authSessionId;
    }

    const url = `${API_BASE_URL}/api/blocks?${params}`;
    console.log('🔲 [API] fetchEnabledBlocks:', url, 'headers:', headers);

    const response = await fetch(url, {
      headers,
      credentials: 'include', // Send cookies for cross-origin requests
    });

    console.log('🔲 [API] fetchEnabledBlocks response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('🔲 [API] fetchEnabledBlocks error response:', errorText);
      let errorMessage = `Failed to fetch blocks: ${response.status}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.error || errorMessage;
      } catch (e) {
        // Not JSON, use text
        if (errorText) errorMessage += ` - ${errorText.substring(0, 200)}`;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log('🔲 [API] fetchEnabledBlocks result:', data);
    return data;
  } catch (error) {
    console.error('Blocks fetch error:', error);
    throw error;
  }
}

/**
 * Fetch data for a single block (for async per-block loading)
 *
 * Each block loads independently, so a failing block doesn't break others.
 *
 * @param {string} clientId - Client identifier
 * @param {string} blockId - Block identifier (e.g., 'member-info')
 * @param {object} options - Optional settings
 * @param {string} options.authSessionId - Auth session ID for authenticated requests
 * @param {Record<string, string>} options.queryParams - Extra query params to append
 * @returns {Promise<{id: string, data: object}>}
 */
export async function fetchBlockData(clientId, blockId, options = {}) {
  try {
    const params = new URLSearchParams({ client_id: clientId });
    if (options.queryParams) {
      for (const [key, value] of Object.entries(options.queryParams)) {
        if (value !== null && value !== undefined && value !== '') {
          params.set(key, value);
        }
      }
    }
    const headers = {};

    // Add auth session header if provided
    if (options.authSessionId) {
      headers['X-Auth-Session-Id'] = options.authSessionId;
    }

    const response = await fetch(`${API_BASE_URL}/api/blocks/${blockId}?${params}`, {
      headers,
      credentials: 'include', // Send cookies for cross-origin requests
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `Failed to fetch block ${blockId}: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Block ${blockId} fetch error:`, error);
    throw error;
  }
}

/**
 * Submit an action for a block (e.g., answer a question)
 *
 * @param {string} blockId - Block identifier
 * @param {object} payload - Action payload including client_id and action
 * @param {object} options - Optional settings
 * @param {string} options.authSessionId - Auth session ID for authenticated requests
 * @returns {Promise<{success: boolean, persisted: boolean, message?: string}>}
 */
export async function blockAction(blockId, payload, options = {}) {
  try {
    const headers = { 'Content-Type': 'application/json' };

    // Add auth session header if provided
    if (options.authSessionId) {
      headers['X-Auth-Session-Id'] = options.authSessionId;
    }

    const response = await fetch(`${API_BASE_URL}/api/blocks/${blockId}/action`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      credentials: 'include', // Send cookies for cross-origin requests
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `Action failed: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Block action error:', error);
    throw error;
  }
}

// ============================================================================
// Inline Follow-up API (loop clarification answer/skip)
// ============================================================================

/**
 * Submit an answer or skip for an inline follow-up question.
 *
 * @param {string} clientId - Client identifier
 * @param {string} anonymousId - Anonymous user identifier
 * @param {string} action - 'answer' or 'skip'
 * @param {string} questionId - The question ID
 * @param {string} answer - The selected answer (for 'answer' action)
 * @param {string} factTemplate - Template for creating the memory fact
 * @param {string} questionText - The question that was asked (for context)
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export async function submitInlineFollowupAction(
  clientId,
  anonymousId,
  action,
  questionId,
  answer = null,
  factTemplate = null,
  questionText = null,
  requestId = null,
) {
  try {
    const payload = {
      client_id: clientId,
      anonymous_id: anonymousId, // Backend expects anonymous_id, not user_id
      action,
      question_id: questionId,
    };

    if (requestId) {
      payload.request_id = requestId;
    }

    if (action === 'answer') {
      payload.answer = answer;
      payload.fact_template = factTemplate;
      payload.question_text = questionText; // Include the question for context
    }

    const response = await fetch(`${API_BASE_URL}/api/blocks/followup/action`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `Follow-up action failed: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Inline follow-up action error:', error);
    throw error;
  }
}

// ============================================================================
// Auth API
// ============================================================================

/**
 * Fetch the member profile for an auth session
 *
 * The backend will validate the session and return the profile.
 * Demo sessions (demo-01, demo-02) don't need client_id.
 * Real SSO sessions require client_id for Gateway validation.
 *
 * @param {string} authSessionId - The auth session ID
 * @param {string} clientId - Client identifier (optional for demo, required for SSO)
 * @returns {Promise<{authenticated: boolean, profile: object|null, source: string|null}>}
 */
export async function fetchAuthProfile(authSessionId, clientId = null) {
  try {
    const headers = {};

    if (authSessionId) {
      headers['X-Auth-Session-Id'] = authSessionId;
    }

    // Build URL with optional client_id parameter
    let url = `${API_BASE_URL}/api/auth/profile`;
    if (clientId) {
      url += `?client_id=${encodeURIComponent(clientId)}`;
    }

    const response = await fetch(url, { headers });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `Failed to fetch profile: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Auth profile fetch error:', error);
    throw error;
  }
}

/**
 * Start the staged mock email verification flow.
 */
export async function requestMockAuthChallenge(clientId, sessionId, email) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/mock/challenge`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        client_id: clientId,
        session_id: sessionId,
        email,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `Failed to start verification: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Mock auth challenge error:', error);
    throw error;
  }
}

/**
 * Fetch safe live-demo payload data for demo-mode clients.
 * Includes contrast scenarios plus separately provisioned hero chat content.
 */
export async function fetchDemoScenarios(clientId, options = {}) {
  try {
    const params = new URLSearchParams({
      client_id: clientId,
    });
    if (options.refresh === true) {
      params.set('refresh', '1');
    }

    const response = await fetch(`${API_BASE_URL}/api/auth/mock/scenarios?${params.toString()}`);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `Failed to load demo scenarios: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Demo scenario fetch error:', error);
    throw error;
  }
}

/**
 * Submit a launch-partner application from the live demo page.
 */
export async function submitLaunchPartnerApplication(payload) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/demo/apply`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `Failed to submit application: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Launch partner application error:', error);
    throw error;
  }
}

/**
 * Verify the staged mock email OTP code.
 */
export async function verifyMockAuthCode(clientId, sessionId, code, anonymousId = null) {
  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/mock/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        client_id: clientId,
        session_id: sessionId,
        code,
        anonymous_id: anonymousId,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `Failed to verify code: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Mock auth verify error:', error);
    throw error;
  }
}

/**
 * What a person has said to this visitor since the browser last looked.
 *
 * A staff reply arrives on WhatsApp, minutes after the request that asked for
 * help has already returned, so there is no other way for the page to hear
 * one. Called on a timer only while a case is open.
 *
 * `readToken` is the per-conversation capability the server minted when the
 * handover opened and returned once, in that turn's response. It is what makes
 * this route safe to point at a visitor's own conversation with the team.
 *
 * Returns an empty array on any failure rather than throwing: a poll that
 * fails is a poll, and the next one is a few seconds away. A visitor waiting
 * for a person must never be shown an error because a background request
 * missed.
 */
export async function fetchWaitingMessages(clientId, sessionId, anonymousId, readToken) {
  return (await fetchWaitingState(clientId, sessionId, anonymousId, readToken)).messages;
}

/**
 * The poll, with what the page needs to know about WhatsApp (Phase 2): whether
 * it may offer "Continuar no WhatsApp", and whether the conversation already
 * moved there, which ends it here; and whether the case closed any other way
 * (`caseClosed`). Never throws, for the reason above.
 */
export async function fetchWaitingState(clientId, sessionId, anonymousId, readToken) {
  const none = {
    messages: [],
    whatsappContinuationAvailable: false,
    continuedOnWhatsapp: false,
    caseClosed: false,
  };
  // The capability is not optional. A poll without one is refused by the
  // server, so sending it would be a request made only to be turned down.
  if (!clientId || !sessionId || !readToken) {
    return none;
  }
  try {
    // POST, so the session id never reaches a URL. What comes back is the
    // visitor's own conversation with the organisation, and a query string
    // ends up in access logs, proxy logs, browser history and Referer headers.
    const response = await fetch(`${API_BASE_URL}/api/conversation/waiting`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: clientId,
        session_id: sessionId,
        anonymous_id: anonymousId,
        read_token: readToken,
      }),
    });
    if (!response.ok) {
      return none;
    }
    const data = await response.json();
    return {
      messages: Array.isArray(data.messages) ? data.messages : [],
      whatsappContinuationAvailable: data.whatsapp_continuation_available === true,
      continuedOnWhatsapp: data.continued_on_whatsapp === true,
      caseClosed: data.case_closed === true,
    };
  } catch {
    return none;
  }
}

/**
 * The visitor's "Nova conversa" while a person is involved (plan
 * docs/plans/2026-10-12-web-chat-refresh, D4): the server closes the case and
 * tells the team. Returns `{ ended }`, or `{ error }` when the case may still be
 * open, so the page does not tell the visitor it ended.
 */
export async function endWebConversation(clientId, sessionId, anonymousId, readToken) {
  if (!clientId || !sessionId || !readToken) {
    console.error('Ending a conversation without its capability', {
      hasClient: Boolean(clientId),
      hasSession: Boolean(sessionId),
      hasToken: Boolean(readToken),
    });
    return { error: 'not_available' };
  }
  try {
    const response = await fetch(`${API_BASE_URL}/api/conversation/end`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: clientId,
        session_id: sessionId,
        anonymous_id: anonymousId,
        read_token: readToken,
      }),
    });
    const data = await response.json();
    if (!response.ok) {
      console.error('Ending the conversation refused', response.status, data.code);
      return { error: data.code || 'request_failed' };
    }
    if (typeof data.ended !== 'boolean') {
      console.error('API contract violation: no `ended` in /api/conversation/end', data);
      return { error: 'request_failed' };
    }
    return { ended: data.ended };
  } catch (error) {
    console.error('Ending the conversation failed', error);
    return { error: 'request_failed' };
  }
}

/**
 * The visitor's own "Continuar no WhatsApp": the server puts an invitation with
 * a link to the business number in this chat and returns it, already marked as
 * read. Returns `{ messages }`, or `{ error }` with the server's code.
 */
export async function requestWhatsappContinuation(clientId, sessionId, anonymousId, readToken) {
  if (!clientId || !sessionId || !readToken) {
    return { error: 'not_available' };
  }
  try {
    const response = await fetch(`${API_BASE_URL}/api/conversation/whatsapp-offer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: clientId,
        session_id: sessionId,
        anonymous_id: anonymousId,
        read_token: readToken,
      }),
    });
    const data = await response.json();
    if (!response.ok) {
      console.error('WhatsApp continuation refused', data.code);
      return { error: data.code || 'request_failed' };
    }
    return { messages: Array.isArray(data.messages) ? data.messages : [] };
  } catch (error) {
    console.error('WhatsApp continuation failed', error);
    return { error: 'request_failed' };
  }
}

/**
 * Send the web handover form (docs/plans/2026-10-11-web-handover-form, contract
 * 3). The case opens here: the turn that showed the form opened nothing. The
 * token is the one that turn returned. Returns `{ data }` on success (the same
 * fields a handover turn returns, with `status`), or `{ error, messages }` with
 * the server's code and any reviewed notice it sent. Never throws: the card
 * shows a calm line and the visitor can send again.
 */
export async function submitHandoverForm(clientId, sessionId, anonymousId, readToken, fields) {
  if (!clientId || !sessionId || !readToken) {
    console.error('Handover form sent without its conversation', {
      hasClient: Boolean(clientId),
      hasSession: Boolean(sessionId),
      hasToken: Boolean(readToken),
    });
    return { error: 'not_available', messages: [] };
  }
  try {
    const response = await fetch(`${API_BASE_URL}/api/conversation/handover`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: clientId,
        session_id: sessionId,
        anonymous_id: anonymousId,
        conversation_read_token: readToken,
        choice: fields.choice,
        name: fields.name || null,
        whatsapp: fields.whatsapp || null,
        email: fields.email || null,
        subject: fields.subject || null,
      }),
    });
    const data = await response.json();
    if (!response.ok) {
      console.error('Handover form refused', response.status, data.code);
      return {
        error: data.code || 'request_failed',
        messages: Array.isArray(data.messages) ? data.messages : [],
      };
    }
    return { data: { ...data, messages: Array.isArray(data.messages) ? data.messages : [] } };
  } catch (error) {
    console.error('Handover form failed', error);
    return { error: 'request_failed', messages: [] };
  }
}
